import React from 'react';
import { Field, ErrorMessage, useField } from 'formik';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';

interface TextAreaFieldProps {
  label: string;
  name: string;
  rows?: number;
  placeholder?: string;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  isRequired?: boolean;
  disabled?: boolean;
  labelSize?: number | string;
  labelWeight?: number | string;
  labelMb?: number | string;
  height?: number | string;
  showHelp?: boolean;
  helpText?: string;
  helpPlacement?: TooltipPlacement;
}

const FieldContainer = styled.div`
  font-family: "Poppins", sans-serif;
`;

const LabelRow = styled.div<{ $mb?: number | string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ $mb }) => ($mb !== undefined ? $mb : '4px')};
`;

const StyledLabel = styled.label<{
  $size?: number | string;
  $weight?: number | string;
}>`
  display: block;
  font-size: ${({ $size }) =>
    $size !== undefined ? (typeof $size === 'number' ? `${$size}px` : $size) : '14px'};
  font-weight: ${({ $weight }) => ($weight !== undefined ? $weight : 600)};
`;

const StyledError = styled.div`
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const StyledTextarea = styled(Field) <{ $error?: boolean; $height?: number | string }>`
  padding: 0.75rem;
  width: 100%;
  box-sizing: border-box;
  border-radius: 5px;
  border: 1px solid ${({ $error }) => ($error ? 'red' : '#ccc')};
  background-color: #fff;
  outline: none;

  font-size: 12px !important;  
  font-family: "Poppins", sans-serif !important;

  &::placeholder {
    color: #999;
    font-family: "Poppins", sans-serif;
    font-size: 14px;
  }


  resize: none;

  /* If a fixed height is provided, apply it; otherwise let rows control height */
  height: ${({ $height }) =>
    $height !== undefined ? (typeof $height === 'number' ? `${$height}px` : $height) : 'auto'};

  /* keep scroll if content exceeds fixed height */
  overflow-y: auto;

  &:focus {
    border-color: ${({ $error }) => ($error ? 'red' : 'rgb(102, 210, 253)')};
    box-shadow: 0 0 0 2px
      ${({ $error }) => ($error ? 'rgba(255, 0, 0, 0.5)' : 'rgb(102, 210, 253)')};
  }
`;

const HelpIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #cfcfcf;
  border-radius: 50%;
  font-size: 11px;
  color: #8b8b8b;
  cursor: pointer;
  user-select: none;
`;

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  label,
  name,
  rows = 4,
  placeholder,
  value,
  onChange,
  onBlur,
  isRequired = false,
  disabled = false,
  labelSize = 14,
  labelWeight = 600,
  labelMb = '4px',
  height,
  showHelp = false,
  helpText,
  helpPlacement = 'topRight',
}) => {
  const [field, meta] = useField(name);

  return (
    <FieldContainer>
      <LabelRow $mb={labelMb}>
        <StyledLabel htmlFor={name} $size={labelSize} $weight={labelWeight}>
          {label}
          {isRequired && <span style={{ color: "red", fontSize: "15px" }}>*</span>}
        </StyledLabel>

        {showHelp && !!helpText && (
          <Tooltip title={helpText} placement={helpPlacement} zIndex={2000}>
            <HelpIcon aria-label="help">?</HelpIcon>
          </Tooltip>
        )}
      </LabelRow>

      <StyledTextarea
        as="textarea"
        id={name}
        name={name}
        rows={rows}
        placeholder={placeholder}
        value={value !== undefined ? value : field.value}
        onChange={onChange || field.onChange}
        onBlur={onBlur || field.onBlur}
        disabled={disabled}
        $error={!!(meta.touched && meta.error)}
        $height={height}
      />

      <ErrorMessage name={name} component={StyledError} />
    </FieldContainer>
  );
};